const SELECTORS = {
  container: '.cl-po--dimension-container',
  item: '.cl-po--dimension-item',
  width: 'input[name*="_dimensions_x"]',
  height: 'input[name*="_dimensions_y"]',
  proportionsControl: '.dimension-proportions-control',
};

const DEFAULTS = {
  height: 3,
  minWidth: 18,
  maxWidth: 24,
  defaultWidth: 18,
  strings: {
    widthNote: 'Please enter a width between 18 and 24 inches',
    heightNote: 'Height is fixed at 3 inches',
  },
};

class BoatRegistrationDimensions {
  constructor(config = {}) {
    this.config = {
      ...DEFAULTS,
      ...config,
      strings: { ...DEFAULTS.strings, ...(config.strings || {}) },
    };

    this.isApplying = false;
    this.applyTimeout = null;
    this.handleFieldEvent = this.handleFieldEvent.bind(this);

    this.init();
  }

  init() {
    this.apply();

    this.observer = new MutationObserver(() => this.scheduleApply());
    this.observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('change', this.handleFieldEvent, true);
    document.addEventListener('focusout', this.handleFieldEvent, true);
  }

  scheduleApply() {
    if (this.isApplying) return;

    clearTimeout(this.applyTimeout);
    this.applyTimeout = setTimeout(() => this.apply(), 100);
  }

  apply() {
    const container = document.querySelector(SELECTORS.container);

    if (!container) return;

    const widthInput = container.querySelector(SELECTORS.width);
    const heightInput = container.querySelector(SELECTORS.height);

    if (!widthInput || !heightInput) {
      console.warn('BoatRegistrationDimensions: could not find the width/height inputs');
      return;
    }

    this.lockHeight(heightInput);

    if (document.activeElement !== widthInput) {
      this.initialiseWidth(widthInput);
    }

    this.addNote(widthInput, 'width', this.config.strings.widthNote);
    this.addNote(heightInput, 'height', this.config.strings.heightNote);

    document.querySelector(SELECTORS.proportionsControl)?.remove();
  }

  lockHeight(input) {
    input.readOnly = true;
    input.setAttribute('aria-readonly', 'true');
    input.setAttribute('tabindex', '-1');
    input.classList.add('brn-dimension__input--locked');

    this.setValue(input, this.config.height);
  }

  initialiseWidth(input) {
    if (!Number.isFinite(parseFloat(input.value))) {
      this.setValue(input, this.config.defaultWidth);
      return;
    }

    this.clampWidth(input);
  }

  clampWidth(input) {
    const { minWidth, maxWidth, defaultWidth } = this.config;
    const raw = parseFloat(input.value);
    const width = Number.isFinite(raw)
      ? Math.min(Math.max(Math.round(raw), minWidth), maxWidth)
      : defaultWidth;

    this.setValue(input, width);
  }

  enforceHeight() {
    const heightInput = document.querySelector(`${SELECTORS.container} ${SELECTORS.height}`);

    if (heightInput) {
      this.setValue(heightInput, this.config.height);
    }
  }

  setValue(input, value) {
    const next = String(value);

    if (input.value === next) return;

    this.isApplying = true;

    try {
      input.value = next;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (error) {
      console.warn('BoatRegistrationDimensions: could not set dimension value', error);
    } finally {
      this.isApplying = false;
    }
  }

  addNote(input, key, text) {
    if (!text) return;

    const item = input.closest(SELECTORS.item) || input.parentElement;

    if (!item || item.querySelector(`[data-brn-note="${key}"]`)) return;

    const note = document.createElement('small');
    const noteId = `brn-note-${key}-${input.id || key}`;

    note.className = 'brn-dimension__note';
    note.dataset.brnNote = key;
    note.id = noteId;
    note.textContent = text;

    item.appendChild(note);
    input.setAttribute('aria-describedby', noteId);
  }

  handleFieldEvent(event) {
    if (this.isApplying) return;

    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches(SELECTORS.width)) {
      this.clampWidth(target);
      this.enforceHeight();
      return;
    }

    if (target.matches(SELECTORS.height)) {
      this.setValue(target, this.config.height);
    }
  }
}

const startBoatRegistrationDimensions = () => {
  new BoatRegistrationDimensions(window.theme?.boatRegistrationDimensions);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBoatRegistrationDimensions);
} else {
  startBoatRegistrationDimensions();
}
